"use client";
import { apiSpecificOffers } from "@/apis/offers";
import { apiExpiredOffers } from "@/apis/user";
import { Offer, OffersOffer, PaginationType } from "@/services/dataTypes";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  filterBannersByDimensions,
  getBannerDimensions,
  getBaseImageUrl,
  getFinalDiscountTag,
} from "@/constants/hooks";
import dynamic from "next/dynamic";
import { apiOfferDetails } from "@/apis/offers";

const Banner = dynamic(() => import("./Banner"), { ssr: false });
const OffersListView = dynamic(() => import("./OffersListView"), { ssr: false });
const OfferModal = dynamic(() => import("./OfferModal"), { ssr: false });
const SimpleOfferModal = dynamic(() => import("./SimpleOfferModal"), { ssr: false });

interface Props {
  initialOffers: OffersOffer[];
  companyId: string;
  merchantId: string;
  awaited_p_id: string;
  mer_slug_type: string;
  mer_slug: string;
  offerBanner: OffersOffer[];
  wideOfferBanners: OffersOffer[];
  domain: string;
  merchantName: string;
  pagination: PaginationType;
  ads_campaign: boolean;
}

const LazyLoadingOffers = ({
  initialOffers,
  companyId,
  merchantId,
  awaited_p_id,
  mer_slug_type,
  mer_slug,
  wideOfferBanners,
  domain,
  merchantName,
  pagination,
  ads_campaign,
}: Props) => {
  const [offers, setOffers] = useState<OffersOffer[]>(initialOffers);
  const [expiredOffers, setExpiredOffers] = useState<OffersOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver | null>(null);
  const [filteredOfferBanners, setFilteredOfferBanners] = useState<OffersOffer[]>([]);
  const bannerDisplayAfter = 2;

  // ── Single modal state (Theme-3 pattern) ──
  // null = closed, non-null = open with this data
  const [modalData, setModalData] = useState<Offer | null>(null);
  const [modalMerchantHref, setModalMerchantHref] = useState<string>("");
  const [modalFinalDiscountTag, setModalFinalDiscountTag] = useState<string | null>(null);
  // Prevents double-click firing multiple fetches
  const [modalLoading, setModalLoading] = useState(false);
  const loadMoreRef = useRef<() => void>(() => {});

  // ── Reset on merchant change (SPA navigation) ──
  useEffect(() => {
    setOffers(initialOffers);
    setPage(1);
    setHasMore(true);
  }, [initialOffers]);

  // ── Fetch expired offers once per merchant ──
  const fetchExpiredOffers = useCallback(async () => {
    try {
      const response = await apiExpiredOffers(merchantId, companyId);
      setExpiredOffers(response.data?.offers || []);
    } catch (error) {
      // silently fail
    }
  }, [merchantId, companyId]);

  useEffect(() => {
    fetchExpiredOffers();
  }, [fetchExpiredOffers]);

  // ── Auto-open modal on page load when URL has show=true ──
  useEffect(() => {
    if (!awaited_p_id || !companyId) return;
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    if (params.get("show") !== "true") return;

    const fetchInitialModal = async () => {
      try {
        const offer_details = await apiOfferDetails(awaited_p_id, companyId);
        if (!offer_details?.data) return;

        const matchingOffer = initialOffers.find(
          (o) => o.offer?.unique_id === awaited_p_id,
        );
        if (matchingOffer) {
          const originalPrice = matchingOffer.offer?.original_price
            ? parseFloat(matchingOffer.offer.original_price)
            : 0;
          const salePrice = matchingOffer.offer?.sale_price
            ? parseFloat(matchingOffer.offer.sale_price)
            : 0;
          const discountPercent =
            originalPrice > 0 && salePrice > 0
              ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
              : null;
          setModalFinalDiscountTag(
            getFinalDiscountTag(
              matchingOffer.offer?.offer_title || matchingOffer.offer?.offer_detail,
              discountPercent,
            ),
          );
        }
        setModalData(offer_details.data);
      } catch (error) {
        console.error("Error fetching offer details on load:", error);
      }
    };

    fetchInitialModal();
  }, [awaited_p_id, companyId]);

  // ── Called when user clicks any card's CTA button ──
  // Uses the CLICKED card's own unique_id — not awaited_p_id
  // modalLoading guard prevents double-click firing multiple fetches
  const handleOfferClick = useCallback(
    async (offer: OffersOffer, merchantHref: string) => {
      if (modalLoading) return;
      setModalLoading(true);
      try {
        const offer_details = await apiOfferDetails(offer.offer.unique_id, companyId);
        if (!offer_details?.data) return;

        const originalPrice = offer.offer?.original_price
          ? parseFloat(offer.offer.original_price)
          : 0;
        const salePrice = offer.offer?.sale_price
          ? parseFloat(offer.offer.sale_price)
          : 0;
        const discountPercent =
          originalPrice > 0 && salePrice > 0
            ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
            : null;

        setModalFinalDiscountTag(
          getFinalDiscountTag(
            offer.offer?.offer_title || offer.offer?.offer_detail,
            discountPercent,
          ),
        );
        setModalMerchantHref(merchantHref);
        setModalData(offer_details.data);
      } catch (error) {
        console.error("Error fetching offer details on click:", error);
      } finally {
        setModalLoading(false);
      }
    },
    [modalLoading, companyId],
  );

  const handleCloseModal = useCallback(() => {
    setModalData(null);
    setModalMerchantHref("");
    setModalFinalDiscountTag(null);
  }, []);

  // ── Infinite scroll (stale-closure-safe via ref) ──
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    if (page === pagination?.last_page) {
      setHasMore(false);
      return;
    }
    setLoading(true);
    try {
      const nextPage = page + 1;
      const response = await apiSpecificOffers(merchantId, companyId, nextPage);
      if (response.data?.offers.length > 0) {
        setOffers((prev) => [...prev, ...response.data.offers]);
        setPage(nextPage);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more offers:", error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, page, pagination?.last_page, merchantId, companyId]);

  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect();
      if (!node || !hasMore) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreRef.current(); // ref — no stale closure
          }
        },
        { threshold: 0.1 },
      );
      observer.current.observe(node);
    },
    [hasMore],
  );

  useEffect(() => {
    if (wideOfferBanners?.length > 0) {
      setFilteredOfferBanners(
        filterBannersByDimensions(wideOfferBanners, 300, 600, 50, 150),
      );
    }
  }, [wideOfferBanners]);

  return (
    <>
      {/* ── ONE modal — null = closed, data = open (Theme-3 pattern) ── */}
      {modalData != null && !ads_campaign && (
        <OfferModal
          data={modalData}
          companyId={companyId}
          onClose={handleCloseModal}
          domain={domain}
          merchantHref={modalMerchantHref}
        />
      )}
      {modalData != null && ads_campaign && (
        <SimpleOfferModal
          data={modalData}
          onClose={handleCloseModal}
          domain={domain}
          merchantHref={modalMerchantHref}
          finalDiscountTag={modalFinalDiscountTag}
        />
      )}

      {/* Loading overlay while fetch is in progress after card click */}
      {modalLoading && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.3)",
            zIndex: 9999,
          }}
        >
          <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#FF5A00]" />
        </div>
      )}

      <section className="w-full py-8 md:py-12 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            <div className="w-full space-y-6">
              {offers.map((offer, index) =>
                offer.status === 1 && offer.offer.status === 1 ? (
                  <React.Fragment key={offer.id}>
                    <div className="w-full">
                      <OffersListView
                        product={offer}
                        companyId={companyId}
                        awaited_p_id={awaited_p_id}
                        mer_slug_type={mer_slug_type}
                        mer_slug={mer_slug}
                        domain={domain}
                        ads_campaign={ads_campaign}
                        onOfferClick={handleOfferClick}
                      />
                    </div>
                    {(index + 1) % bannerDisplayAfter === 0 &&
                      filteredOfferBanners.length > 0 &&
                      filteredOfferBanners[Math.floor(index / bannerDisplayAfter)] &&
                      (() => {
                        const banner =
                          filteredOfferBanners[Math.floor(index / bannerDisplayAfter)];
                        const dimension = getBannerDimensions(banner);
                        return (
                          <div
                            className="w-full flex justify-center py-4"
                            key={`banner-${Math.floor(index / bannerDisplayAfter)}`}
                          >
                            <Banner
                              data={banner}
                              height={dimension?.height}
                              width={dimension?.width}
                              offerLink={null}
                              domain={domain}
                              mer_slug={mer_slug}
                              slug_type={mer_slug_type}
                            />
                          </div>
                        );
                      })()}
                  </React.Fragment>
                ) : null,
              )}

              <div ref={sentinelRef} style={{ height: "1px" }} aria-hidden="true" />

              {loading && (
                <div className="flex justify-center items-center py-10 w-full">
                  <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#FF5A00]" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {expiredOffers?.length > 0 && (
        <section className="w-full py-12 md:py-20 bg-neutral-50/60 border-t border-neutral-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-8">
              <div className="text-center mb-4">
                <h2 className="text-2xl md:text-3xl font-black text-neutral-950 tracking-tight leading-tight">
                  Old deals & coupons that might still work for{" "}
                  <span className="text-[#FF5A00] tracking-normal inline-block relative after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-[#FF5A00]/10">
                    {merchantName}
                  </span>
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {expiredOffers.slice(0, 10).map((item, i) => (
                  <OffersListView
                    key={item.id}
                    product={item}
                    companyId={companyId}
                    awaited_p_id={awaited_p_id}
                    mer_slug_type={mer_slug_type}
                    mer_slug={mer_slug}
                    domain={domain}
                    ads_campaign={ads_campaign}
                    onOfferClick={handleOfferClick}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        id="AggregateOffer"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AggregateOffer",
            name: `${merchantName} Coupons & Deals`,
            description: `Currently, there are several active coupons and deals available for ${merchantName} products.`,
            offerCount: offers.length,
            offers: offers.map((offer) => ({
              "@type": "Offer",
              name: offer?.offer?.offer_title,
              description: offer?.offer?.offer_detail,
              url: getBaseImageUrl(`${domain}`, `${offer.offer?.url}`, ""),
            })),
          }),
        }}
      />
    </>
  );
};

export default LazyLoadingOffers;
